import store from '@state/store'
import Vue from 'vue'
//! NOTE: icons come from feather-icons

// auth related routes
const authRoutes = [
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/pages/account/login')),
    meta: {
      name: 'login',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Pedidos' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/forget-password',
    name: 'forget-password',
    component: () =>
      lazyLoadView(import('@views/pages/account/forgetPassword')),
    meta: {
      name: 'forget-password',
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Pedidos' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      name: 'logout',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(
          authRequiredOnPreviousRoute ? { name: 'dashboard' } : { ...routeFrom }
        )
      },
    },
  },
]

// error pages
const errorPagesRoutes = [
  {
    path: '/404',
    name: '404',
    component: require('@views/pages/secondary/error-404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  {
    path: '/500',
    name: '500',
    component: require('@views/pages/secondary/error-500').default,
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// dashboard
const dashboardRoutes = [
  {
    path: '/',
    name: 'Inicio',
    icon: 'home',
    component: () => lazyLoadView(import('@views/pages/dashboard/dashboard')),
    meta: {
      name: 'Inicio',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/hasCollectionAdminCapabilities'] && store.state.userChoice === Vue.prototype.$administrative) {
          next({ name: 'admin_dashboard' })
        } else if (store.getters['auth/hasCollectionExecutiveCapabilities'] && store.state.userChoice === Vue.prototype.$administrative) {
          next({ name: 'executive_dashboard' })
        } else if (store.getters['auth/hasCollectionAdminCapabilities'] && store.state.userChoice === Vue.prototype.$prejudicial) {
          next({ name: 'prejudicial_admin_dashboard' })
        } else if (store.getters['auth/hasCollectionExecutiveCapabilities'] && store.state.userChoice === Vue.prototype.$prejudicial) {
          next({ name: 'prejudicial_executive_dashboard' })
        }
        else {
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const orderAppsRoutes = [
  {
    path: '/apps/orders',
    header: 'Informes',
    name: 'Pedidos',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/orders/orders')),
    meta: {
      name: 'Pedidos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user is allowed to see report routes
        if (!store.getters['auth/hasReportCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const reportAppsRoutes = [
  {
    path: '/apps/reports',
    name: 'Informes',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/reports/reports')),
    meta: {
      name: 'Informes',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (!store.getters['auth/hasReportCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const debtCollectionClientRoutes = [
  {
    path: '/apps/debt_collections/clients/invoices',
    header: 'Cobranza Administrativa',
    name: 'Facturas',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/invoices')),
    meta: {
      name: 'Facturas',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/debt_collections/clients/debtors',
    name: 'Deudores-Cliente',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/debtors')),
    meta: {
      name: 'Deudores',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/debt_collections/clients/checks',
    name: 'Cheques',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/checks')),
    meta: {
      name: 'Cheques',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/debt_collections/clients/export_to_csv',
    name: 'client_export_to_csv',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/export_to_csv')),
    meta: {
      name: 'Exportar a CSV',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/client/debt_collection/:company_id/:invoice_id',
    name: 'client_debt_collection#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/debt_collection_show')),
    meta: {
      name: 'Cobranza#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/client/debtor/:person_id',
    name: 'client_debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/clients/debtor_show')),
    meta: {
      name: 'Debtor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasDebtCollectionCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]
const debtCollectionMassUploadRoutes = [
  {
    path: '/apps/mass_uploads/debt_collections',
    header: 'Cargas Masivas',
    name: 'mass_uploads_debt_collection_dashboard',
    icon: 'layout',
    component: () => lazyLoadView(import('@views/pages/apps/mass_uploads/debt_collections')),
    meta: {
      name: 'Carga de cobranzas',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/mass_uploads/debtor_contacts',
    name: 'mass_uploads_debtor_contacts_dashboard',
    icon: 'layout',
    component: () => lazyLoadView(import('@views/pages/apps/mass_uploads/debtor_contacts')),
    meta: {
      name: 'Carga de contactos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    }
  },
  {
    path: '/apps/mass_uploads/debt_collections/:mass_upload_id',
    name: 'dc_mass_uploads#show',
    component: () => lazyLoadView(import('@views/pages/apps/mass_uploads/dc_mass_upload_show')),
    meta: {
      name: 'Carga Masiva#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/mass_uploads/debtor_contacts/:mass_upload_id',
    name: 'debtor_contacts_mass_upload#show',
    component: () => lazyLoadView(import('@views/pages/apps/mass_uploads/debtor_contact_mass_upload_show')),
    meta: {
      name: 'Carga Masiva#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]


const debtCollectionAdminRoutes = [
  {
    path: '/apps/admin/debt_collections/dashboard',
    header: 'Cobranza Administrativa',
    name: 'admin_dashboard',
    icon: 'home',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/dashboard')),
    meta: {
      name: 'Inicio',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections/executives',
    name: 'admin_executives',
    icon: 'users',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/executives')),
    meta: {
      name: 'Mis Ejecutivos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections/export_to_csv',
    name: 'admin_export_to_csv',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/export_to_csv')),
    meta: {
      name: 'Exportar a CSV',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections/creditors',
    name: 'admin_creditors',
    icon: 'plus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditors')),
    meta: {
      name: 'Acreedores',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections/debtors',
    name: 'admin_debtors',
    icon: 'minus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtors')),
    meta: {
      name: 'Deudores',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections/debtor_contacts',
    name: 'admin_debtor_contacts',
    icon: 'user-check',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtor_contacts.vue')),
    meta: {
      name: 'Datos de Contacto',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debt_collections',
    name: 'admin_debt_collections',
    icon: 'trending-up',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debt_collection_index')),
    meta: {
      name: 'Cobranzas',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: 'late_invoices',
        name: 'admin_late_invoices',
        icon: 'alert-triangle',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/late_invoices')),
        meta: {
          name: 'Facturas Atrasadas',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionAdminCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'today_invoices',
        name: 'admin_today_invoices',
        icon: 'calendar',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/today_invoices')),
        meta: {
          name: 'Facturas Para Hoy',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionAdminCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'future_invoices',
        name: 'admin_future_invoices',
        icon: 'fast-forward',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/future_invoices')),
        meta: {
          name: 'Facturas Futuras',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionAdminCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'conflicted_invoices',
        name: 'admin_conflicted_invoices',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/conflicted_invoices')),
        meta: {
          name: 'Facturas En Conflicto',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionAdminCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
      path: 'unassigned_invoices',
      name: 'admin_unassigned_invoices',
      icon: 'pending',
      component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/unassigned_invoices')),
      meta: {
        name: 'Facturas sin asignar',
        authRequired: true,
        beforeResolve(routeTo, routeFrom, next) {
          // Check if user has report service available
          if (!store.getters['auth/hasCollectionAdminCapabilities']) {
            // Continue to the login page
            next({ name: 'Inicio' })
          } else {
            // Redirect to the home page instead
            next()
          }
        }
      },
      props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
    ]
  },
  {
    path: '/apps/admin/debt_collection/:company_id/:invoice_id',
    name: 'admin_debt_collections#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debt_collection_show')),
    meta: {
      name: 'Cobranza#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/creditor/:company_id/',
    name: 'admin_creditors#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditor_show')),
    meta: {
      name: 'Creditor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/debtor/:person_id',
    name: 'admin_debtors#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtor_show')),
    meta: {
      name: 'Debtor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/creditor/:company_id/debtor/:person_id',
    name: 'admin_creditor_debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditor_debtor_show')),
    meta: {
      name: 'Creditor_Debtor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/conflicts',
    name: 'admin_conflicts',
    icon: 'alert-octagon',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicts')),
    meta: {
      name: 'Conflictos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/admin/payment_types',
    name: 'admin_payment_types',
    icon: 'dollar-sign',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/payment_types')),
    meta: {
      name: 'Tipos de Pago',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const debtCollectionExecutiveRoutes = [
  {
    path: '/apps/executive/debt_collections/dashboard',
    header: 'Cobranza Administrativa',
    name: 'executive_dashboard',
    icon: 'home',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/dashboard')),
    meta: {
      name: 'Inicio',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debt_collections/export_to_csv',
    name: 'executive_export_to_csv',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/export_to_csv')),
    meta: {
      name: 'Exportar a CSV',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debt_collections/creditors',
    name: 'executive_creditors',
    icon: 'plus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditors')),
    meta: {
      name: 'Acreedores',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debt_collections/debtors',
    name: 'executive_debtors',
    icon: 'minus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtors')),
    meta: {
      name: 'Deudores',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debt_collections/debtor_contacts',
    name: 'executive_debtor_contacts',
    icon: 'user-check',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtor_contacts.vue')),
    meta: {
      name: 'Datos de Contacto',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debt_collections',
    name: 'executive_debt_collections',
    icon: 'trending-up',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debt_collection_index')),
    meta: {
      name: 'Gestiones/Cobranzas',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: 'late_invoices',
        name: 'executive_late_invoices',
        icon: 'alert-triangle',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/late_invoices')),
        meta: {
          name: 'Facturas Atrasadas',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'today_invoices',
        name: 'executive_today_invoices',
        icon: 'calendar',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/today_invoices')),
        meta: {
          name: 'Facturas Para Hoy',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'future_invoices',
        name: 'executive_future_invoices',
        icon: 'fast-forward',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/future_invoices')),
        meta: {
          name: 'Facturas Futuras',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'conflicted_invoices',
        name: 'executive_conflicted_invoices',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicted_invoices')),
        meta: {
          name: 'Facturas En Conflicto',
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Check if user has report service available
            if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
              // Continue to the login page
              next({ name: 'Inicio' })
            } else {
              // Redirect to the home page instead
              next()
            }
          }
        },
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
    ]
  },
  {
    path: '/apps/executive/debt_collection/:company_id/:invoice_id',
    name: 'executive_debt_collections#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debt_collection_show')),
    meta: {
      name: 'Cobranza#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/creditor/:company_id',
    name: 'executive_creditors#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditor_show')),
    meta: {
      name: 'Creditor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/debtor/:person_id',
    name: 'executive_debtors#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtor_show')),
    meta: {
      name: 'Debtor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/creditor/:company_id/debtor/:person_id',
    name: 'executive_creditor_debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditor_debtor_show')),
    meta: {
      name: 'Creditor_Debtor#show',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/executive/conflicts',
    name: 'executive_conflicts',
    icon: 'alert-octagon',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicts')),
    meta: {
      name: 'Conflictos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  }, 
  {
    path: '/apps/executive/contact_record_actions',
    name: 'executive_contact_record_actions',
    icon: 'phone-call',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/contact_record_actions')),
    meta: {
      name: 'Gestiones Rápidas',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const prejudicialExecutiveRoutes = [

  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/dashboard',
    name: 'prejudicial_executive_dashboard',
    header: 'Cobranza Prejudicial',
    metaName: 'Inicio',
    icon: 'home',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/dashboard')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/export_to_csv',
    name: 'prejudicial_executive_export_to_csv',
    metaName: 'Exportar a CSV',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/export_to_csv')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/clients',
    name: 'prejudicial_executive_clients',
    metaName: 'Clientes',
    icon: 'circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/prejudicial_clients')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/creditors',
    name: 'prejudicial_executive_creditors',
    metaName: 'Acreedores',
    icon: 'plus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditors')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/debtors',
    name: 'prejudicial_executive_debtors',
    metaName: 'Deudores',
    icon: 'minus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtors')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections/debtor_contacts',
    name: 'prejudicial_executive_debtor_contacts',
    metaName: 'Datos de Contacto',
    icon: 'user-check',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtor_contacts.vue')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collections',
    name: 'prejudicial_executive_debt_collections',
    metaName: 'Gestiones/Cobranzas',
    icon: 'trending-up',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debt_collection_index')),
    children: [
      generateExecutiveRoute({
        path: 'late_invoices',
        name: 'prejudicial_executive_late_invoices',
        metaName: 'Facturas Atrasadas',
        icon: 'alert-triangle',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/late_invoices')),
      }),
      generateExecutiveRoute({
        path: 'today_invoices',
        name: 'prejudicial_executive_today_invoices',
        metaName: 'Facturas Para Hoy',
        icon: 'calendar',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/today_invoices')),
      }),
      generateExecutiveRoute({
        path: 'future_invoices',
        name: 'prejudicial_executive_future_invoices',
        metaName: 'Facturas Futuras',
        icon: 'fast-forward',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/future_invoices')),
      }),
      generateExecutiveRoute({
        path: 'work_orders',
        name: 'prejudicial_executive_work_orders',
        metaName: 'OT',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/work_orders')),
      }),
      generateExecutiveRoute({
        path: 'conflicted_invoices',
        name: 'prejudicial_executive_conflicted_invoices',
        metaName: 'Facturas En Conflicto',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicted_invoices')),
      }),
    ]
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debt_collection/:company_id/:invoice_id',
    name: 'prejudicial_executive_debt_collections#show',
    metaName: 'Cobranza#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debt_collection_show')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/client/:client_id/',
    name: 'prejudicial_executive_clients#show',
    metaName: 'Client#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/prejudicial_client_show')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/creditor/:company_id/',
    name: 'prejudicial_executive_creditors#show',
    metaName: 'Creditor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditor_show')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/debtor/:person_id',
    name: 'prejudicial_executive_debtors#show',
    metaName: 'Debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/debtor_show')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/creditor/:company_id/debtor/:person_id',
    name: 'prejudicial_executive_creditor_debtor#show',
    metaName: 'Creditor_Debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/creditor_debtor_show')),
  }),
  generateExecutiveRoute({
    path: '/apps/prejudicial/executive/conflicts',
    name: 'prejudicial_executive_conflicts',
    metaName: 'Conflictos',
    icon: 'alert-octagon',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicts')),
  }),
  generateExecutiveRoute({
    path: '/apps/executive/contact_record_actions',
    name: 'prejudicial_executive_contact_record_actions',
    metaName: 'Gestiones Rápidas',
    icon: 'phone-call',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/contact_record_actions')),
  })
]

const prejudicialAdminRoutes = [
  
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/dashboard',
    name: 'prejudicial_admin_dashboard',
    header: 'Cobranza Prejudicial',
    metaName: 'Inicio',
    icon: 'home',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/dashboard')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/executives',
    name: 'prejudicial_admin_executives',
    metaName: 'Mis Ejecutivos',
    icon: 'users',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/executives')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/export_to_csv',
    name: 'prejudicial_admin_export_to_csv',
    metaName: 'Exportar a CSV',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/export_to_csv')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/clients',
    name: 'prejudicial_admin_prejudicial_clients',
    metaName: 'Clientes',
    icon: 'circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/prejudicial_clients')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/creditors',
    name: 'prejudicial_admin_creditors',
    metaName: 'Acreedores',
    icon: 'plus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditors')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/debtors',
    name: 'prejudicial_admin_debtors',
    metaName: 'Deudores',
    icon: 'minus-circle',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtors')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections/debtor_contacts',
    name: 'prejudicial_admin_debtor_contacts',
    metaName: 'Datos de Contacto',
    icon: 'user-check',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtor_contacts.vue')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collections',
    name: 'prejudicial_admin_debt_collections',
    metaName: 'Cobranzas',
    icon: 'trending-up',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debt_collection_index')),
    children: [
      generateAdminRoute({
        path: 'late_invoices',
        name: 'prejudicial_admin_late_invoices',
        metaName: 'Facturas Atrasadas',
        icon: 'alert-triangle',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/late_invoices')),
      }),
      generateAdminRoute({
        path: 'today_invoices',
        name: 'prejudicial_admin_today_invoices',
        metaName: 'Facturas Para Hoy',
        icon: 'calendar',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/today_invoices')),
      }),
      generateAdminRoute({
        path: 'future_invoices',
        name: 'prejudicial_admin_future_invoices',
        metaName: 'Facturas Futuras',
        icon: 'fast-forward',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/future_invoices')),
      }),
      generateAdminRoute({
        path: 'work_orders',
        name: 'prejudicial_admin_work_orders',
        metaName: 'OT',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/work_orders')),
      }),
      generateAdminRoute({
        path: 'unassigned_invoices',
        name: 'prejudicial_admin_unassigned_invoices',
        metaName: 'Facturas sin asignar',
        icon: 'pending',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/unassigned_invoices')),
      }),
      generateAdminRoute({
        path: 'conflicted_invoices',
        name: 'prejudicial_admin_conflicted_invoices',
        metaName: 'Facturas En Conflicto',
        icon: 'pause',
        component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/conflicted_invoices')),
      }),
    ]
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debt_collection/:company_id/:invoice_id',
    name: 'prejudicial_admin_debt_collections#show',
    metaName: 'Cobranza#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debt_collection_show')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/client/:client_id/',
    name: 'prejudicial_admin_clients#show',
    metaName: 'Client#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/prejudicial_client_show')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/creditor/:company_id/',
    name: 'prejudicial_admin_creditors#show',
    metaName: 'Creditor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditor_show')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/debtor/:person_id',
    name: 'prejudicial_admin_debtors#show',
    metaName: 'Debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/debtor_show')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/creditor/:company_id/debtor/:person_id',
    name: 'prejudicial_admin_creditor_debtor#show',
    metaName: 'Creditor_Debtor#show',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/admin/creditor_debtor_show')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/conflicts',
    name: 'prejudicial_admin_conflicts',
    metaName: 'Conflictos',
    icon: 'alert-octagon',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/conflicts')),
  }),
  generateAdminRoute({
    path: '/apps/prejudicial/admin/payment_types',
    name: 'prejudicial_admin_payment_types',
    metaName: 'Tipos de Pago',
    icon: 'dollar-sign',
    component: () => lazyLoadView(import('@views/pages/apps/debt_collections/executive/payment_types')),
  }),
]

  const checkRoutes = [
  {
    path: '/apps/checks/invoices',
    header: 'Cheques',
    name: 'Facturas Pendientes',
    icon: 'watch',
    component: () => lazyLoadView(import('@views/pages/apps/checks/debt_collections')),
    meta: {
      name: 'Facturas Pendientes',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/checks/check_collections',
    name: 'Retiro de cheques',
    icon: 'truck',
    component: () => lazyLoadView(import('@views/pages/apps/checks/check_collections')),
    meta: {
      name: 'Retiro de cheques',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/checks/checks-route',
    name: 'Hoja de ruta',
    icon: 'edit-2',
    component: () => lazyLoadView(import('@views/pages/apps/checks/checks-route')),
    meta: {
      name: 'Hoja de ruta',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/checks',
    name: 'Custodia de Cheques',
    icon: 'briefcase',
    component: () => lazyLoadView(import('@views/pages/apps/checks/checks')),
    meta: {
      name: 'Custodia de Cheques',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/checks/bank_accounts',
    name: 'Cuentas Bancarias',
    icon: 'user-plus',
    component: () => lazyLoadView(import('@views/pages/apps/checks/bank_accounts')),
    meta: {
      name: 'Cuentas Bancarias',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/apps/checks/export_to_csv',
    name: 'Exportar a CSV',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/checks/export_to_csv')),
    meta: {
      name: 'Exportar a CSV',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/hasCheckCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities']) {
          next()
        } else {
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

const toolRoutes = [
  {
    path: '/apps/tools/official_journal',
    header: 'Herramientas',
    name: 'Diario Oficial',
    icon: 'tool',
    component: () => lazyLoadView(import('@views/pages/apps/tools/official_journal_search')),
    meta: {
      name: 'Diario Oficial',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasCollectionExecutiveCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities'] || store.getters['auth/hasReportExecutiveCapabilities'] || store.getters['auth/hasCheckCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/reports/old_reports/mass_upload',
    header: 'Informes Antiguos',
    name: 'old_reports_mass_upload',
    icon: 'download',
    component: () => lazyLoadView(import('@views/pages/apps/old_reports/mass_upload.vue')),
    meta: {
      name: 'Carga de informes antiguos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasReportAdminCapabilities']) {
          next({ name: 'Inicio' })
        } else {
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  // Commented in case someday far away we want tutorials again....
  // {
  //   path: '/apps/tools/tutorials',
  //   name: 'Tutoriales',
  //   icon: 'youtube',
  //   component: () => lazyLoadView(import('@views/pages/apps/tools/tutorials')),
  //   meta: {
  //     name: 'Tutoriales',
  //     authRequired: true,
  //     beforeResolve(routeTo, routeFrom, next) {
  //       // Check if user has report service available
  //       if (store.getters['auth/hasCollectionExecutiveCapabilities'] || store.getters['auth/hasCollectionAdminCapabilities'] || store.getters['auth/hasReportExecutiveCapabilities'] || store.getters['auth/hasCheckCapabilities']) {
  //         // Continue to the login page
  //         next()
  //       } else {
  //         // Redirect to the home page instead
  //         next({ name: 'Inicio' })
  //       }
  //     }
  //   },
  //   props: (route) => ({ user: store.state.auth.currentUser || {} }),
  // },
]

const reportExcecutiveRoutes = [
  {
    path: '/apps/orders/full_report_orders',
    header: 'Pedidos Clientes',
    name: 'full_report_orders',
    icon: 'file',
    component: () => lazyLoadView(import('@views/pages/apps/orders/full-report-orders')),
    meta: {
      name: 'Informes Completos',
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has report service available
        if (store.getters['auth/hasReportExecutiveCapabilities']) {
          // Continue to the login page
          next()
        } else {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  }
]

const reportsRoutes = [
  ...orderAppsRoutes,
  ...reportAppsRoutes,
]

const appsRoutes = [
  ...orderAppsRoutes,
  ...reportAppsRoutes,
  ...debtCollectionClientRoutes,
  ...debtCollectionAdminRoutes,
  ...debtCollectionExecutiveRoutes,
  ...debtCollectionMassUploadRoutes,
  ...prejudicialExecutiveRoutes,
  ...prejudicialAdminRoutes,
  ...checkRoutes,
  ...toolRoutes,
  ...reportExcecutiveRoutes,
]

const authProtectedRoutes = [
  ...dashboardRoutes,
]

const allRoutes = [...authRoutes, ...authProtectedRoutes, ...errorPagesRoutes, ...appsRoutes]

export { allRoutes, authProtectedRoutes, reportsRoutes, debtCollectionClientRoutes,
   debtCollectionAdminRoutes, prejudicialExecutiveRoutes, prejudicialAdminRoutes, checkRoutes, debtCollectionExecutiveRoutes, 
   debtCollectionMassUploadRoutes, toolRoutes, reportExcecutiveRoutes }

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@components/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

function generateAdminRoute({path, header, icon, name, metaName, component, children}) {
  const route = {
    path,
    name,
    icon,
    component: component,
    meta: {
      name: metaName,
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionAdminCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  };

  if (header) {
    route.header = header
  }

  if (children) {
    route.children = children
  }
  return route;
}

function generateExecutiveRoute({path, header, icon, name, metaName, component, children}) {
  const route = {
    path,
    name,
    icon,
    component: component,
    meta: {
      name: metaName,
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Check if user has debt collections service available
        if (!store.getters['auth/hasCollectionExecutiveCapabilities']) {
          // Redirect to the home page instead
          next({ name: 'Inicio' })
        } else {
          // Continue to the login page
          next()
        }
      }
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  };

  if (header) {
    route.header = header
  }

  if (children) {
    route.children = children
  }
  return route;
}
